import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, User, Loader2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SmartImage from '../components/SmartImage';
import ShareButton from '../components/ShareButton';
import api, { mediaUrl } from '../lib/api';
import { sanitizeHtml } from '../lib/sanitize';
import { toResourceEmbed, isDirectVideo } from '../lib/embed';

// Set / restore a <meta name="..."> or property tag on the document head.
const setMeta = (attr, key, content) => {
  if (!content) return null;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  const created = !el;
  if (!el) { el = document.createElement('meta'); el.setAttribute(attr, key); document.head.appendChild(el); }
  const prev = el.getAttribute('content');
  el.setAttribute('content', content);
  return { el, prev, created };
};

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api.get(`/blog/posts/${slug}`)
      .then((res) => setPost(res.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    api.get('/blog/posts').then((res) => setRelated((res.data || []).filter((p) => p.slug !== slug).slice(0, 3))).catch(() => {});
  }, [slug]);

  // SEO: real <title> + meta description + Open Graph, restored on unmount.
  useEffect(() => {
    if (!post) return undefined;
    const prevTitle = document.title;
    document.title = post.seo_title || `${post.title} — Osho Universal`;
    const desc = post.seo_description || post.excerpt || '';
    const img = post.cover_image ? mediaUrl(post.cover_image) : '';
    const handles = [
      setMeta('name', 'description', desc),
      setMeta('property', 'og:title', post.seo_title || post.title),
      setMeta('property', 'og:description', desc),
      setMeta('property', 'og:type', 'article'),
      setMeta('property', 'og:image', img),
    ].filter(Boolean);
    return () => {
      document.title = prevTitle;
      handles.forEach(({ el, prev, created }) => {
        if (created) el.remove();
        else if (prev != null) el.setAttribute('content', prev);
      });
    };
  }, [post]);

  if (loading) return <div className="App"><Header /><div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-mahogany" /></div><Footer /></div>;
  if (notFound || !post) return (
    <div className="App"><Header />
      <div className="text-center py-32 bg-cream">
        <p className="text-mahogany/70">Post not found.</p>
        <Link to="/blog" className="pill-btn mt-4 inline-flex">Back to Blog</Link>
      </div><Footer />
    </div>
  );

  const dateStr = post.published_at ? new Date(post.published_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '';
  const videoEmbed = post.video_url ? toResourceEmbed(post.video_url) : '';

  return (
    <div className="App" data-testid="page-blog-post">
      <Header />
      <main className="bg-cream">
        <article className="max-w-[820px] mx-auto px-6 lg:px-10 py-12">
          <Link to="/blog" className="inline-flex items-center gap-2 text-mahogany/70 hover:text-mahogany mb-6" data-testid="blog-back-link"><ArrowLeft className="w-4 h-4" /> All Posts</Link>

          <div className="eyebrow mb-3">{post.category}</div>
          <h1 className="font-serif-display text-4xl md:text-5xl text-burgundy leading-tight" data-testid="blog-post-title">{post.title}</h1>
          <div className="flex items-center justify-between gap-4 mt-4 flex-wrap">
            <div className="flex items-center gap-2 text-sm text-mahogany/70">
              <User className="w-4 h-4" /> {post.author} {dateStr && <><span className="opacity-50">·</span> {dateStr}</>}
            </div>
            <ShareButton title={post.title} text={post.title} testid="share-blog" />
          </div>

          {/* Vlog video takes priority as the hero when present, else cover image */}
          {post.video_url ? (
            <div className="mt-8 rounded-3xl overflow-hidden bg-black aspect-video" data-testid="blog-video">
              {videoEmbed
                ? <iframe src={videoEmbed} title={post.title} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                : (isDirectVideo(post.video_url) || /\/uploads\//.test(post.video_url))
                  ? <video src={mediaUrl(post.video_url)} controls preload="metadata" playsInline poster={post.cover_image ? mediaUrl(post.cover_image) : undefined} className="w-full h-full" data-testid="blog-video-player" />
                  : <a href={post.video_url} target="_blank" rel="noopener noreferrer" className="text-cream p-6 block">Watch the video →</a>}
            </div>
          ) : post.cover_image && <SmartImage src={mediaUrl(post.cover_image)} alt={post.title} className="aspect-[16/9] rounded-3xl overflow-hidden mt-8" />}

          {/* Audio blog player */}
          {post.audio_url && (
            <div className="mt-6 rounded-2xl border border-rose/40 bg-white/70 p-4" data-testid="blog-audio">
              <div className="text-xs uppercase tracking-widest text-mahogany/70 mb-2">Listen to this post</div>
              <audio controls src={mediaUrl(post.audio_url)} className="w-full" />
            </div>
          )}

          <div className="prose-osho mt-10 text-burgundy/85 leading-relaxed text-[15px] md:text-base"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(post.content) }} />

          {post.tags?.length > 0 && (
            <div className="mt-10 flex flex-wrap gap-2">
              {post.tags.map((t) => <span key={t} className="text-xs px-3 py-1 rounded-full bg-rose-soft text-mahogany">#{t}</span>)}
            </div>
          )}
        </article>

        {related.length > 0 && (
          <section className="max-w-[1300px] mx-auto px-6 lg:px-10 pb-20">
            <h2 className="font-serif-display text-3xl text-burgundy mb-6">Related <span className="font-italic-display text-rose-italic">Posts</span></h2>
            <div className="grid md:grid-cols-3 gap-8">
              {related.map((p) => (
                <Link key={p.slug} to={`/blog/${p.slug}`} data-testid={`related-${p.slug}`}
                  className="group block bg-white/70 rounded-3xl overflow-hidden border border-rose/40 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                  <SmartImage src={mediaUrl(p.cover_image)} alt={p.title} className="aspect-[4/3]">
                    <div className="absolute top-3 left-3 z-10 rounded-full bg-cream/90 text-mahogany text-[11px] uppercase tracking-widest px-3 py-1">{p.category}</div>
                  </SmartImage>
                  <div className="p-6">
                    <h3 className="font-serif-display text-xl text-burgundy leading-tight mb-2 group-hover:text-rose-italic transition-colors">{p.title}</h3>
                    <span className="inline-flex items-center gap-1.5 text-mahogany font-medium text-sm">Read More <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default BlogPost;
